import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { fetchChatwootSso } from "./fetchChatwootSso";
import type { ChatwootEmbedProps, ChatwootEmbedRef } from "./types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { Inbox, AlertCircle, RefreshCw } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

// Chatwoot base URL
const CHATWOOT_BASE_URL = "https://inbox.zaviago.com/app";

// Session storage keys — scoped per user to prevent cross-user leaks (OPS-1163).
const CHATWOOT_AUTH_SESSION_PREFIX = "chatwoot_authenticated_";
const CHATWOOT_ACCOUNT_PREFIX = "chatwoot_account_";

// The "authenticated" flag only proves we SSO'd at some point in this tab —
// the REAL login is a cookie inside the inbox.zaviago.com iframe with its own
// expiry. Trusting the flag forever strands long-lived tabs on Chatwoot's
// login page once that cookie dies (user report 2026-09-14: chat bubble →
// /app/login; re-login fixed it because logout clears the flag). Bound the
// trust window so stale tabs transparently re-run SSO instead.
const CHATWOOT_AUTH_TTL_MS = 8 * 60 * 60 * 1000; // 8h < Chatwoot cookie life

function isChatwootAuthenticatedThisSession(userEmail: string): boolean {
  try {
    const raw = sessionStorage.getItem(CHATWOOT_AUTH_SESSION_PREFIX + userEmail);
    if (!raw) return false;
    if (raw === "true") return false; // legacy flag without timestamp — re-SSO once
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > CHATWOOT_AUTH_TTL_MS) {
      sessionStorage.removeItem(CHATWOOT_AUTH_SESSION_PREFIX + userEmail);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function markChatwootAuthenticated(userEmail: string, accountId?: string): void {
  try {
    sessionStorage.setItem(CHATWOOT_AUTH_SESSION_PREFIX + userEmail, String(Date.now()));
    if (accountId) {
      sessionStorage.setItem(CHATWOOT_ACCOUNT_PREFIX + userEmail, accountId);
    }
  } catch {
    // Ignore
  }
}

function getStoredAccountId(userEmail: string): string | null {
  try {
    return sessionStorage.getItem(CHATWOOT_ACCOUNT_PREFIX + userEmail);
  } catch {
    return null;
  }
}

/**
 * Build the account-specific Chatwoot dashboard URL.
 * Forces Chatwoot to load the correct tenant's account instead of the user's
 * default account — prevents cross-tenant data leaks when a user belongs to
 * multiple Chatwoot accounts (OPS-1163).
 */
function buildDashboardUrl(accountId: string | null, subPath?: string): string {
  if (accountId) {
    const base = `${CHATWOOT_BASE_URL}/accounts/${accountId}`;
    return subPath ? `${base}${subPath}` : `${base}/dashboard`;
  }
  // Fallback: generic URL (only if account_id is unknown)
  return subPath ? `${CHATWOOT_BASE_URL}${subPath}` : CHATWOOT_BASE_URL;
}

/**
 * Clear all Chatwoot session flags from sessionStorage.
 * Called on logout to ensure next /inbox visit triggers fresh SSO auth.
 * Clears ALL chatwoot_authenticated_* and chatwoot_account_* keys (not just
 * current user) to prevent orphaned sessions from previous users.
 */
export function clearChatwootSession(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CHATWOOT_AUTH_SESSION_PREFIX) || key?.startsWith(CHATWOOT_ACCOUNT_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore — sessionStorage may be unavailable
  }
}

export const ChatwootEmbed = forwardRef<ChatwootEmbedRef, ChatwootEmbedProps>(
  function ChatwootEmbed(props, ref) {
    const { className, initialPath, onReady, onError } = props;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Track SSO flow: SSO loads login page first (onLoad #1), then redirects
    // to dashboard (onLoad #2). We keep the overlay until the redirect completes.
    const isSsoFlow = useRef(false);
    const loadCount = useRef(0);

    // State
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [iframeReady, setIframeReady] = useState(false);

    // GOTCHA G13: currentTenant is a string (tenant name), not an object
    const currentTenant = useAuthStore((state) => state.currentTenant);
    const username = useAuthStore((state) => state.username);
    const isProvisioned = useAuthStore((state) => state.isProvisioned);

    // Store account_id from SSO response so handleIframeLoad can persist it
    const pendingAccountId = useRef<string | undefined>(undefined);

    // Check auth and set iframe URL on mount
    useEffect(() => {
      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      // Check if already authenticated this session
      // This avoids the login flash when navigating back to /inbox
      if (isChatwootAuthenticatedThisSession(username)) {
        console.log("[ChatwootEmbed] Already authenticated this session, loading dashboard directly");
        isSsoFlow.current = false;
        loadCount.current = 0;
        // G8 guard: reject paths that could cause open redirect
        const safePath = initialPath && !initialPath.includes('://') && !initialPath.startsWith('//')
          ? initialPath
          : undefined;
        // Use account-specific URL to ensure correct tenant's inbox (OPS-1163)
        const storedAccountId = getStoredAccountId(username);
        setIframeSrc(buildDashboardUrl(storedAccountId, safePath));
        setIsLoading(false);
        return;
      }

      // Not authenticated this session - fetch SSO URL for authentication.
      // If initialPath contains a conversation ID, pass it as sso_conversation_id
      // so Chatwoot redirects to that conversation after SSO login (OPS-1477).
      console.log("[ChatwootEmbed] Not authenticated this session, fetching SSO URL");
      isSsoFlow.current = true;
      loadCount.current = 0;
      let cancelled = false;
      setIsLoading(true);
      setLoadError(null);

      fetchChatwootSso(currentTenant, username)
        .then((data) => {
          if (!cancelled) {
            let ssoUrl = data.sso_url;
            // Store account_id so handleIframeLoad can persist it to sessionStorage
            pendingAccountId.current = data.account_id;
            // Extract conversation ID from deep link path and append to SSO URL
            // Chatwoot's login page natively supports sso_conversation_id query param
            const convMatch = initialPath?.match(/\/conversations\/(\d+)/);
            if (convMatch) {
              const separator = ssoUrl.includes("?") ? "&" : "?";
              ssoUrl = `${ssoUrl}${separator}sso_conversation_id=${convMatch[1]}`;
            }
            setIframeSrc(ssoUrl);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("[ChatwootEmbed] SSO fetch error:", err);
            setLoadError(err instanceof Error ? err.message : "Failed to get SSO URL");
            setIsLoading(false);
            onError?.(err instanceof Error ? err.message : "SSO error");
          }
        });

      return () => {
        cancelled = true;
      };
    }, [currentTenant, username, initialPath, onError]);

    // Handle iframe load
    const handleIframeLoad = useCallback(() => {
      loadCount.current++;

      // SSO flow: first onLoad is the login page (before redirect to dashboard).
      // Keep the overlay visible until the 2nd onLoad (the dashboard).
      if (isSsoFlow.current && loadCount.current < 2) {
        return;
      }

      // Mark as authenticated for this session so we don't re-auth on navigation.
      // Store account_id so the fast-path always navigates to the correct tenant (OPS-1163).
      markChatwootAuthenticated(username ?? "", pendingAccountId.current);

      // After SSO completes, navigate to the intended deep-link path.
      // SSO always redirects to /dashboard — non-conversation deep-links are lost.
      // Fix: reload iframe with the intended URL now that auth cookies are set.
      if (isSsoFlow.current && initialPath && !initialPath.match(/\/conversations\/\d+/)) {
        isSsoFlow.current = false;
        const accountId = pendingAccountId.current || getStoredAccountId(username ?? "");
        setIframeSrc(buildDashboardUrl(accountId, initialPath));
        return; // Wait for the next onLoad with the deep-linked page
      }

      setIframeReady(true);
      onReady?.();
    }, [onReady, initialPath, username]);

    // Handle iframe error
    const handleIframeError = useCallback(() => {
      setLoadError("Failed to load Inbox. Please check your connection.");
      onError?.("iframe load error");
    }, [onError]);

    // Force reload - refetch SSO URL to get fresh token (for re-authentication)
    const reload = useCallback(() => {
      setLoadError(null);
      setIframeReady(false);
      setIframeSrc(null);
      setIsLoading(true);

      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      // Always fetch fresh SSO URL on reload (user explicitly wants to re-auth)
      isSsoFlow.current = true;
      loadCount.current = 0;
      fetchChatwootSso(currentTenant, username)
        .then((data) => {
          pendingAccountId.current = data.account_id;
          setIframeSrc(data.sso_url);
          setIsLoading(false);
          setIframeKey((prev) => prev + 1);
        })
        .catch((err) => {
          console.error("[ChatwootEmbed] SSO fetch error:", err);
          setLoadError(err instanceof Error ? err.message : "Failed to get SSO URL");
          setIsLoading(false);
          onError?.(err instanceof Error ? err.message : "SSO error");
        });
    }, [currentTenant, username, onError]);

    // Expose ref API
    useImperativeHandle(
      ref,
      () => ({
        reload,
        isReady: iframeReady,
      }),
      [reload, iframeReady]
    );

    // GOTCHA G5/G13: Check provisioning via authStore.isProvisioned
    if (!isProvisioned) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <Inbox className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Inbox Not Available
          </h2>
          <p className="text-gray-500 max-w-md">
            Customer support inbox has not been set up for your workspace.
            Please contact your administrator.
          </p>
        </div>
      );
    }

    // Loading SSO URL state
    if (isLoading) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full bg-gray-50",
            className
          )}
        >
          <AnimatedStarburst size={48} className="mb-4" />
          <p className="text-gray-500">Loading inbox...</p>
        </div>
      );
    }

    // Error state
    if (loadError) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Unable to load inbox
          </h2>
          <p className="text-gray-500 max-w-md mb-4">{loadError}</p>
          <button
            onClick={reload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    // No iframe URL available
    if (!iframeSrc) {
      return (
        <div
          className={cn(
            "flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50",
            className
          )}
        >
          <AlertCircle className="w-16 h-16 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Unable to Connect
          </h2>
          <p className="text-gray-500 max-w-md mb-4">
            Unable to connect to inbox. Please try again.
          </p>
          <button
            onClick={reload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    // Render iframe with dashboard URL (if authenticated) or SSO URL (if not)
    return (
      <div
        className={cn(
          "chatwoot-embed-wrapper relative w-full h-full min-h-[400px] bg-gray-50",
          className
        )}
      >
        {/* Show loading overlay while iframe is loading */}
        {!iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="flex flex-col items-center">
              <AnimatedStarburst size={48} className="mb-4" />
              <p className="text-gray-500">Connecting to inbox...</p>
            </div>
          </div>
        )}
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={iframeSrc}
          className="absolute inset-0 w-full h-full border-none"
          title="Chatwoot Inbox"
          allow="microphone; camera; clipboard-read; clipboard-write"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
    );
  }
);

ChatwootEmbed.displayName = "ChatwootEmbed";
